/**
 * Notice that this tab is running a build the site no longer serves.
 *
 * WHY THE EXISTING PATH IS NOT ENOUGH. `sw-register.ts` offers an update when a
 * new service worker takes over, via `controllerchange`. That is the right
 * trigger for the normal case and it does nothing at all for the case that
 * actually bites: a client whose service worker never updates. Production on
 * 2026-09-17 had students on `index-CUlsKCUn.js` while the site served
 * `index-BuwjsuF-.js`, days apart, running entirely from service-worker cache.
 * Their bundle's assets no longer exist on the CDN, so they were missing every
 * fix shipped since, and nothing in the app could tell them.
 *
 * This asks the SITE what it serves rather than asking the service worker
 * whether it has changed, so a stuck worker cannot hide the answer.
 *
 * The network call and the decision are deliberately separate: the decision is
 * pure and tested, because the failure mode here is a check that cannot fail.
 */

const ENTRY_RE = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/;

/** The entry filename the served HTML points at, or null if it cannot be read. */
export function entryFromHtml(html: string): string | null {
  return ENTRY_RE.exec(html)?.[1] ?? null;
}

/**
 * Whether to tell the student their app is out of date.
 *
 * Every uncertain case answers NO. A prompt shown wrongly costs a student a
 * reload mid-question and teaches them to ignore the next one, so anything we
 * cannot read confidently is treated as "fine":
 *
 *   - a local dev build, which has no hash to compare
 *   - HTML we could not parse an entry out of, including the SPA fallback that
 *     the CDN serves for a missing asset
 *   - any case where the two agree
 */
export function isStale(runningBuildId: string, servedEntry: string | null): boolean {
  if (!servedEntry) return false;
  if (!runningBuildId || runningBuildId === "dev") return false;
  const served = ENTRY_RE.exec(`/assets/${servedEntry}`)?.[1];
  if (!served) return false;
  return !served.includes(runningBuildId);
}

/**
 * Fetch the served HTML and decide. Returns false on any network trouble:
 * offline is the common case and is not evidence of a stale build.
 */
export async function checkStale(
  runningBuildId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    // no-store AND a cache-buster: the service worker is the thing under
    // suspicion, so a request it could answer from its own cache would return
    // exactly the stale HTML we are trying to detect.
    const res = await fetchImpl(`/?_bc=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return false;
    return isStale(runningBuildId, entryFromHtml(await res.text()));
  } catch {
    return false;
  }
}
